import { escapeHtml, sanitizeJournalHtml } from '@/lib/journal/journalRichText';

const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4']);

function isBlankElement(el: Element): boolean {
  const text = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  if (text.length > 0) return false;
  return true;
}

function isReadHeading(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  if (HEADING_TAGS.has(tag)) return true;
  if (tag !== 'p') return false;
  const children = Array.from(el.childNodes).filter((node) => {
    if (node.nodeType === Node.TEXT_NODE) return Boolean(node.textContent?.trim());
    return node.nodeType === Node.ELEMENT_NODE;
  });
  if (children.length !== 1 || children[0].nodeType !== Node.ELEMENT_NODE) return false;
  const inner = (children[0] as Element).tagName.toLowerCase();
  return inner === 'strong' || inner === 'b';
}

function sectionHasContent(start: Element): boolean {
  let sib = start.nextElementSibling;
  while (sib && !isReadHeading(sib)) {
    if (!isBlankElement(sib)) return true;
    sib = sib.nextElementSibling;
  }
  return false;
}

export function stripEmptyJournalReadSections(html: string): {
  html: string;
  hiddenHeadings: string[];
} {
  const sanitized = sanitizeJournalHtml(html);
  if (!sanitized) return { html: '', hiddenHeadings: [] };
  const doc = new DOMParser().parseFromString(`<div id="jf-root">${sanitized}</div>`, 'text/html');
  const root = doc.getElementById('jf-root');
  if (!root) return { html: sanitized, hiddenHeadings: [] };

  const hiddenHeadings: string[] = [];
  for (const el of Array.from(root.children)) {
    if (!root.contains(el) || !isReadHeading(el)) continue;
    if (sectionHasContent(el)) continue;
    const title = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (title) hiddenHeadings.push(title);
    // Drop the empty section and the spacer lines that belonged to it.
    // Blank lines among real content stay — they are template spacing.
    let sib = el.nextElementSibling;
    el.remove();
    while (sib && !isReadHeading(sib) && isBlankElement(sib)) {
      const next = sib.nextElementSibling;
      sib.remove();
      sib = next;
    }
  }

  return { html: root.innerHTML, hiddenHeadings };
}

function inlineMarkdown(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>');
}

export function journalMarkdownToReadHtml(markdown: string): string {
  const normalized = markdown.replace(/\r\n/g, '\n');
  const lines = normalized.split('\n');
  if (normalized.endsWith('\n')) lines.pop();
  const out: string[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    out.push(`<p>${inlineMarkdown(paragraph.join(' '))}</p>`);
    paragraph = [];
  };
  const flushList = () => {
    if (list.length === 0) return;
    out.push(`<ul>${list.map((item) => `<li>${inlineMarkdown(item)}</li>`).join('')}</ul>`);
    list = [];
  };

  for (const line of lines) {
    const heading = /^(#{1,4})\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1].length;
      out.push(`<h${level}>${escapeHtml(heading[2].trim())}</h${level}>`);
      continue;
    }
    const item = /^\s*[-*+]\s+(.+)$/.exec(line);
    if (item) {
      flushParagraph();
      list.push(item[1]);
      continue;
    }
    if (!line.trim()) {
      flushParagraph();
      flushList();
      out.push('<p><br></p>');
      continue;
    }
    flushList();
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();
  return out.join('');
}

export function journalReadHtml(
  html: string | null | undefined,
  markdown: string,
): { html: string; hiddenHeadings: string[] } {
  const fromHtml = sanitizeJournalHtml(html);
  const source = fromHtml || journalMarkdownToReadHtml(markdown);
  return stripEmptyJournalReadSections(source);
}

function clipPreview(text: string, max: number): string {
  const compact = text.replace(/\s+/g, ' ').trim();
  if (!compact) return '';
  if (compact.length <= max) return compact;
  return `${compact.slice(0, max).trim()}…`;
}

export function journalNotePlainPreview(contentMd: string, max = 180): string {
  const withoutHeadings = contentMd
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((line) => !/^\s{0,3}#{1,6}\s+/.test(line))
    .join('\n')
    .replace(/[`*_#>]+/g, ' ')
    .replace(/^\s*[-*+]\s+/gm, ' ')
    .replace(/^\s*\d+\.\s+/gm, ' ');
  return clipPreview(withoutHeadings, max);
}

export function journalEntryListPreview(
  html: string | null | undefined,
  markdown: string,
  max = 180,
): string {
  const sanitized = sanitizeJournalHtml(html);
  if (sanitized) {
    const doc = new DOMParser().parseFromString(
      `<div id="jf-root">${sanitized}</div>`,
      'text/html',
    );
    const root = doc.getElementById('jf-root');
    root?.querySelectorAll('h1,h2,h3,h4').forEach((el) => el.remove());
    const text = clipPreview(root?.textContent ?? '', max);
    if (text) return text;
  }
  return journalNotePlainPreview(markdown, max);
}
